import { useNewRelic } from "@/composables/useNewRelic";

const isDevelopment: boolean = import.meta.env.VITE_APP_DEV === "true";
const isDesktopJourneyEnabled: boolean = import.meta.env.VITE_APP_ENABLE_DESKTOP_JOURNEY === "true";

const isMobileUA = (): boolean => {
  return !!navigator.userAgent.match(/Mobile/i);
};

const hasTouchScreen = (): boolean => {
  let result = false;

  if (window.PointerEvent && "maxTouchPoints" in navigator) {
    // if Pointer Events are supported, just check maxTouchPoints
    if (navigator.maxTouchPoints > 1) {
      result = true;
    }
  } else {
    // no Pointer Events...
    if (window.matchMedia && window.matchMedia("(any-pointer:coarse)").matches) {
      // check for any-pointer:coarse which mostly means touchscreen
      result = true;
    } else if (window.TouchEvent || "ontouchstart" in window) {
      // last resort - check for exposed touch events API / event handler
      result = true;
    }
  }

  return result;
};

export const scrollWithDelay = (target: HTMLElement, footerHeight: number) => {
  setTimeout(() => {
    target.scrollIntoView({ behavior: "smooth", block: "center" });
    window.scrollBy({ top: footerHeight / 2, behavior: "smooth" });
  }, 300);
};

const isMobile = isMobileUA() && hasTouchScreen();

export type Channel = "desktop" | "mobile";
const channel: Channel = isMobile ? "mobile" : "desktop";

interface Bike {
  imageUrl?: string | null;
}

export const useUtils = () => {
  const { newrelic } = useNewRelic();
  const utils = {
    isDevelopment,
    isDesktopJourneyEnabled,
    isMobile,
    channel,

    addPageAction(actionName: string, metadata: Record<string, any> = {}) {
      utils.log({ actionName, metadata });
      if (isDevelopment) return;

      newrelic?.addPageAction(actionName, { browser: navigator.userAgent, ...metadata });
    },

    noticeError(error: any, metadata: Record<string, any> = {}) {
      if (isDevelopment) return;

      newrelic?.log(error, { level: "ERROR", customAttributes: metadata });
      newrelic?.noticeError(error, metadata);
    },

    remoteLog(message: any, options: Record<string, any> = {}) {
      if (isDevelopment) return;

      newrelic?.log(message, options);
    },

    log(message: any) {
      utils.remoteLog(message);
      // if (!isDevelopment) return;
    },

    logError(message: any) {
      utils.remoteLog(message, { level: "error" });
      if (!isDevelopment) return;

      console.error(message);
    },

    bikeImageUrl(bike?: Bike | null): string {
      if (!bike || !bike.imageUrl) {
        utils.log("Can't get bike image URL: bike is null or bike.imageUrl is null");
        return "";
      }

      if (bike.imageUrl.startsWith("http")) {
        utils.log(`Bike image is already an URL: ${bike.imageUrl}`);
        return bike.imageUrl;
      }

      const cdnBikeImage = `https://cdn.bikehero.com.br/bike/${bike.imageUrl}`;
      utils.log(`Bike image URL: ${cdnBikeImage}`);

      return cdnBikeImage;
    },

    transpPixel(): string {
      return "@/assets/images/transp.gif";
    },

    whatsAppLink(phone?: string, text?: string): string {
      if (!phone) return "";
      const phoneNumbers = phone.replace(/\D/g, "");

      text = utils.fullyDecodeURI(text || "");
      text = encodeURIComponent(text);

      return `https://wa.me/${phoneNumbers}?text=${text}`;
    },

    openWhatsApp(phone?: string, text?: string): void {
      const link = utils.whatsAppLink(phone, text);
      if (!link) return;

      window.open(link, "_blank");
    },

    openBikeHeroWhatsApp(): void {
      utils.openWhatsApp(import.meta.env.VITE_APP_BKHR_MAIN_PHONE);
    },

    isEncoded(uri?: string): boolean {
      uri = uri || "";

      return uri !== decodeURIComponent(uri);
    },

    fullyDecodeURI(uri?: string): string {
      uri = uri || "";

      while (utils.isEncoded(uri)) {
        uri = decodeURIComponent(uri);
      }

      return uri;
    },
  };

  return utils;
};

export type UseUtils = ReturnType<typeof useUtils>;
